import React, { FC } from 'react';
import { Image } from 'common/components';
import { countFormatter, dateFormatter, priceFormatter } from 'common/helpers/formatter.helper';
import { VISIBLE_IMAGES_LIMIT_3, VISIBLE_IMAGES_LIMIT_4 } from 'common/config';
import { ArrowRightLong, FileAttachment, Report } from 'common/svg';
import { IRequestHistoryGroup } from 'entities/RequestHistory/RequestHistory.models';
import { ERequestHistoryType } from 'entities/RequestHistory/RequestHistory.enum';
import { getRequestHistoryItemDescription, renderRequestHistoryItemIcon } from 'entities/RequestHistory/RequestHistory.helper';
import { getUserName } from 'entities/User/User.helper';
import { SupplyStatus } from 'entities/Supply/components/SupplyStatus';
import { IFile } from 'entities/File/File.models';

interface IComponentProps {
  item: IRequestHistoryGroup;
  downloadFile?: (file: IFile) => void;
}

export const RequestHistoryItem: FC<IComponentProps> = ({ item, downloadFile }) => {
  const userName = item.user
    ? getUserName.fullLastNameAndInitials(item.user.lastName, item.user.firstName, item.user.patronymic)
    : 'Пользователь удалён';
  const date = dateFormatter('DD MMMM YYYY • HH:mm:ss', item.createdAt);
  const images = item.logs.map((i) => i.goodImage);
  const visibleImages = images.length > VISIBLE_IMAGES_LIMIT_4 ? images.slice(0, VISIBLE_IMAGES_LIMIT_3) : images;
  const hiddenImagesCount = images.length - visibleImages.length;
  const showComment = [ERequestHistoryType.RequestCreated, ERequestHistoryType.Comment].includes(
    item.type as ERequestHistoryType,
  );
  const showImages = [
    ERequestHistoryType.SupplyStatusChanged,
    ERequestHistoryType.SupplyAddedToRequest,
    ERequestHistoryType.SupplyPriceChanged,
  ].includes(item.type as ERequestHistoryType);
  const showSupplyStatusChanges = item.type === ERequestHistoryType.SupplyStatusChanged;
  const showAddedSupplyDetails = item.type === ERequestHistoryType.SupplyAddedToRequest;
  const showSupplyPriceChanges = item.type === ERequestHistoryType.SupplyPriceChanged;

  return (
    <div className="mb-9 request-history__item">
      <div className="request-history__item-icon">{renderRequestHistoryItemIcon(item.type)}</div>

      <div className="request-history__item-container">
        <div className="mb-4 request-history__item-header">
          {item.isNew && <div className="new-indicator" style={{ marginRight: 8 }} />}

          <span className="text-controls-accent">{userName}</span>

          <span className="text-controls color-dark-grey">{getRequestHistoryItemDescription(item.type)}</span>
        </div>

        {showComment && (
          <>
            {!!item.comment?.length && <div className="text-controls mb-4 request-history__item-comment">{item.comment}</div>}

            {!!item.files.length && (
              <div className="mb-4 request-history__item-files">
                {item.files.map((file) => {
                  return (
                    <div key={file.id} className="mb-4 request-history__item-files-item">
                      <FileAttachment className="icon-file-attachment-dark-grey" />

                      <span className="text-tag ellipsis" style={{ flex: 1 }} title={file.name}>
                        {file.name}
                      </span>

                      <Report className="icon-report-dark-grey cursor-pointer" onClick={() => downloadFile?.(file)} />
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {showImages && (
          <div className="mb-4 request-history__item-images">
            {visibleImages.map((src, index) => {
              return <Image key={index} size={32} src={src} />;
            })}

            {!!hiddenImagesCount && (
              <div className="text-tag-accent color-dark-grey request-history__item-images-count">{`+${hiddenImagesCount}`}</div>
            )}
          </div>
        )}

        {showSupplyStatusChanges && (
          <div className="mb-4 request-history__item-changes">
            <SupplyStatus status={item.changes.prevStatus} />

            <ArrowRightLong className="icon-arrow-right-long-dark-grey" />

            <SupplyStatus status={item.changes.currentStatus} isCancelledBySeller={item.isCancelledBySeller} />
          </div>
        )}

        {showAddedSupplyDetails && (
          <div className="mb-4 request-history__item-changes">
            <span className="text-controls">
              {`${countFormatter(item.changes?.count)} за ${priceFormatter(item.changes?.price)}/ед.`}
            </span>
          </div>
        )}

        {showSupplyPriceChanges && (
          <div className="mb-4 request-history__item-changes">
            <span className="text-controls">{priceFormatter(item.changes?.oldPrice)}</span>

            <ArrowRightLong className="icon-arrow-right-long-dark-grey" />

            <span className="text-controls">{priceFormatter(item.changes.price)}</span>
          </div>
        )}

        <div className="text-tag color-dark-grey">{date}</div>
      </div>
    </div>
  );
};
